import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Crowd } from "../components/Crowd";
import { Disclaimer } from "../components/Disclaimer";
import { Arrow, CheckMark, PersonaIcon } from "../components/Icons";
import { Replay } from "../components/Replay";
import { Reveal, useInView } from "../components/Reveal";
import { RunList } from "../components/RunList";
import { Failure, RunListSkeleton } from "../components/States";
import { Tape } from "../components/Tape";
import { prefersReducedMotion } from "../components/use-playback";
import { fetchFeatured, fetchRunDocument, fetchRuns } from "../lib/api";
import { previewLayout } from "../lib/crowd";
import { modelLabel } from "../lib/format";
import { useAsync } from "../lib/use-async";
import styles from "./HomePage.module.css";

const FALLBACK_SCENARIO = "Fictional token FIZZ lists on a thin pool with a 3% creator tax. The team is anonymous.";
const FALLBACK_HASH = "0x68ea84e1f53885ece4be0e38f0e22e940992505ce5fa4ff3af251837eafd9430";

const PERSONAS = [
  ["sniper", "Snipers", "Into a fresh pool in the first rounds, out on the first strong run-up."],
  ["holder", "Holders", "Build a position and keep it through the noise, watching the team more than the price."],
  ["panic_seller", "Panic sellers", "Avoid losses above all, and leave fast when the price drops or fear spreads."],
  ["skeptic", "Skeptics", "Look for red flags first. They trade rarely, in small size, and say what they find."],
] as const;

const crowdPreview = previewLayout(48);

export function HomePage() {
  const featured = useAsync(async () => {
    const summary = await fetchFeatured();
    return summary ? { summary, run: await fetchRunDocument(summary.runId) } : null;
  }, "featured");
  const recent = useAsync(() => fetchRuns(null, 6), "recent");
  const data = featured.status === "ready" ? featured.data : null;

  return (
    <>
      <section className={styles.hero}>
        <h1 className={styles.display}>Launch it in a parallel world first.</h1>
        <div className={styles.intro}>
          <p className={styles.lede}>
            Put a token launch, a rumor or a listing in front of a crowd of AI traders. They post, argue and swap against real
            AMM math on a private fork of Robinhood Chain. Every run is hashed, so anyone can check it.
          </p>
          <div className={styles.actions}>
            <Link to="/new" className="btn btn-primary" viewTransition>
              Run a simulation
            </Link>
            <Link to="/runs" className="btn btn-ghost" viewTransition>
              Browse runs
            </Link>
          </div>
        </div>
      </section>

      <section className={styles.stage} aria-label="The featured run">
        {featured.status === "loading" && <div className={`${styles.placeholder} card skeleton`} role="status" aria-label="Loading the featured run…" />}
        {featured.status === "error" && <Failure message={featured.error.message} onRetry={featured.reload} />}
        {featured.status === "ready" && !data && (
          <p className={`${styles.none} card`}>No run is published yet. The first published run will play here.</p>
        )}
        {data && (
          <>
            <div className={styles.caption}>
              <span className={styles.playing}>
                Playing now · {data.run.crowd.size} agents · {data.run.rounds.length} rounds · {modelLabel(data.run.crowd.model.agents)}
                {data.run.world.forkBlock === null && " · this run on a local test chain, not a fork"}
              </span>
              <Link to={`/run/${data.summary.runId}`} className="arrow-link" viewTransition>
                {data.run.scenario.title} <Arrow />
              </Link>
            </div>
            <Replay run={data.run} variant="hero" />
            <Disclaimer />
          </>
        )}
      </section>

      {data && <Tape run={data.run} />}

      <section className={styles.section} aria-labelledby="how-title">
        <Reveal className={styles.sectionHead}>
          <h2 id="how-title">How a run works</h2>
          <p>You write one scenario and get back one file that anyone can check.</p>
        </Reveal>
        <ol className={styles.steps}>
          <Reveal as="li" index={0} className={styles.step}>
            <ScenarioVisual text={data?.run.scenario.title ?? FALLBACK_SCENARIO} />
            <h3>
              <span className={styles.n}>1</span> Describe the moment
            </h3>
            <p>A token launch, a rumor, a listing. Plain text, plus events that land in the rounds you choose.</p>
          </Reveal>
          <Reveal as="li" index={1} className={styles.step}>
            <div className={styles.visual} aria-hidden="true">
              <Crowd layout={crowdPreview} fill={0.55} maxPitch={25} labels={false} ariaLabel="" />
            </div>
            <h3>
              <span className={styles.n}>2</span> The crowd plays it out
            </h3>
            <p>Every round, each agent reads the feed and the pool, then posts, replies, likes and makes at most one trade. The trades stay on the private fork.</p>
          </Reveal>
          <Reveal as="li" index={2} className={styles.step}>
            <HashVisual hash={data?.run.hash ?? FALLBACK_HASH} />
            <h3>
              <span className={styles.n}>3</span> Anyone can check it
            </h3>
            <p>The run is one file with one hash. Replay its trades, recompute the hash, and compare.</p>
          </Reveal>
        </ol>
      </section>

      <section className={styles.section} aria-labelledby="crowd-title">
        <Reveal className={styles.sectionHead}>
          <h2 id="crowd-title">Four kinds of trader, one feed</h2>
          <p>Agents are archetypes with generated handles, never real people.</p>
        </Reveal>
        <ul className={styles.personas}>
          {PERSONAS.map(([id, name, line], i) => (
            <Reveal as="li" key={id} index={i} className={styles.persona}>
              <span className={styles.personaIcon} aria-hidden="true">
                <PersonaIcon persona={id} size={18} />
              </span>
              <h3>{name}</h3>
              <p>{line}</p>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="recent-title">
        <Reveal className={styles.recentHead}>
          <h2 id="recent-title">Recent runs</h2>
          <Link to="/runs" className="arrow-link" viewTransition>
            All runs <Arrow />
          </Link>
        </Reveal>
        {recent.status === "loading" && <RunListSkeleton />}
        {recent.status === "error" && <Failure message={recent.error.message} onRetry={recent.reload} />}
        {recent.status === "ready" && <RunList runs={recent.data.runs} />}
      </section>

      <Reveal as="section" className={styles.closing} aria-labelledby="closing-title">
        <h2 id="closing-title">What would the crowd do with yours?</h2>
        <Link to="/new" className="btn btn-primary" viewTransition>
          Run a simulation
        </Link>
      </Reveal>
    </>
  );
}

function ScenarioVisual({ text }: { text: string }) {
  const [ref, seen] = useInView<HTMLDivElement>(0.3);
  const reduced = useMemo(prefersReducedMotion, []);
  const [typed, setTyped] = useState(reduced ? text.length : 0);
  useEffect(() => {
    if (!seen || reduced) return;
    setTyped(0);
    const id = window.setInterval(() => {
      setTyped((count) => {
        if (count >= text.length) window.clearInterval(id);
        return Math.min(text.length, count + 1);
      });
    }, 28);
    return () => window.clearInterval(id);
  }, [seen, reduced, text]);
  return (
    <div ref={ref} className={styles.visual} aria-hidden="true">
      <span className={styles.fakeLabel}>Scenario</span>
      <span className={styles.fakeText}>
        {text.slice(0, typed)}
        <i className={styles.caret} />
      </span>
    </div>
  );
}

/** The run's hash resolving out of noise, then checked. */
function HashVisual({ hash }: { hash: string }) {
  const [ref, seen] = useInView<HTMLDivElement>(0.3);
  const reduced = useMemo(prefersReducedMotion, []);
  const shown = hash.slice(0, 26);
  const [resolved, setResolved] = useState(reduced ? shown.length : 0);
  useEffect(() => {
    if (!seen || reduced) return;
    setResolved(2);
    const id = window.setInterval(() => {
      setResolved((count) => {
        if (count >= shown.length) window.clearInterval(id);
        return Math.min(shown.length, count + 1);
      });
    }, 45);
    return () => window.clearInterval(id);
  }, [seen, reduced, shown.length]);
  const noise = "0123456789abcdef";
  return (
    <div ref={ref} className={styles.visual} aria-hidden="true">
      <span className={styles.fakeLabel}>keccak256(run.json)</span>
      <span className={`${styles.fakeHash} mono`}>
        {[...shown].map((char, i) => (i < resolved ? char : noise[(i * 7 + resolved) % 16])).join("")}…
      </span>
      <span className={`${styles.matches} ${resolved >= shown.length ? styles.matchesOn : ""}`}>
        <CheckMark size={12} /> Hash matches
      </span>
    </div>
  );
}
